import type { GetImpulseFormOutput, GetImpulseFormOutputVerbose } from "~/tools/impulse-form";

import type { GaugeChart } from "../../../../common/dashboard-spec/chart-schemas";
import { WidgetColor } from "../../../widget-form/color/form-state";
import { GaugeChartFormState } from "../../../widget-form/gauge-chart-form-state";
import type { WidgetFormSync } from "../../../widget-form/sync";
import type { FormProcessor } from "../../_form-processor";
import { StepFormState as ThresholdStepFormState } from "../_thresholds/step-form-state";

type Spec = GaugeChart["spec"];

const DEFAULT = GaugeChartFormState.default;

function specToInput({ format, max, calculation, thresholds }: Spec) {
	return {
		unit: format?.unit ?? DEFAULT.unit,
		decimals: format?.decimalPlaces?.toString() ?? DEFAULT.decimals,
		max: max?.toString() ?? DEFAULT.max,
		calculation: calculation ?? DEFAULT.calculation,
		color: thresholds?.defaultColor ?? DEFAULT.color,

		thresholds: {
			mode: thresholds?.mode ?? DEFAULT.thresholds.mode,
			steps: thresholds?.steps.map(ThresholdStepFormState.specToInput) ?? DEFAULT.thresholds.steps,
		},
	} satisfies WidgetFormSync;
}

type FormState = GaugeChartFormState;

function outputToSpec({ unit, decimals, max, calculation, color, thresholds }: GetImpulseFormOutput<FormState>): Spec {
	return {
		max,
		calculation,

		format: {
			unit,
			decimalPlaces: decimals,
		},

		thresholds: {
			defaultColor: color,
			mode: thresholds.mode,
			steps: thresholds.steps.map(ThresholdStepFormState.outputToSpec),
		},
	};
}

function verboseOutputToSpec({
	unit,
	decimals,
	max,
	calculation,
	color,
	thresholds,
}: GetImpulseFormOutputVerbose<FormState>): Spec {
	return {
		calculation: calculation ?? undefined,
		max: max ?? undefined,

		format: {
			unit: unit ?? undefined,
			decimalPlaces: decimals ?? undefined,
		},

		thresholds: {
			defaultColor: color ?? WidgetColor.healthy,
			mode: thresholds.mode ?? undefined,
			steps: thresholds.steps.map(ThresholdStepFormState.verboseOutputToSpec),
		},
	};
}

type Input = ReturnType<typeof specToInput>;

function init(sync?: Input): FormState {
	const initial = sync == null ? undefined : GaugeChartFormState.fromSync(sync);

	return GaugeChartFormState.init(initial);
}

function setInput(input: Input, state: FormState): void {
	GaugeChartFormState.setInput(GaugeChartFormState.fromSync(input), state);
}

function resetToDefault(state: FormState): void {
	GaugeChartFormState.setInput(GaugeChartFormState.default, state);
}

const FormState = {
	specToInput,
	outputToSpec,
	verboseOutputToSpec,
	init,
	setInput,
	resetToDefault,
} satisfies FormProcessor<Spec, FormState, Input>;

export { FormState };
